import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DateRange, Locale } from "@/shared/utils";
import { usePagination } from "@/shared/hooks";
import { matchesDateRange, normalizeDateToStartOfDay, normalizeDateToEndOfDay, toNumber } from "@/shared/utils";
import { useGetInvoicesQuery } from "../api";
import type { PurchaseStatus, InvoiceCategory, PurchaseListItem } from "../types";
import { transactionStatusToPurchaseStatus } from "../types/api";
import { usePurchasesSort, toggleSort, type SortKey, type SortConfig } from "./usePurchasesSort";

export interface PurchaseStats {
  totalServices: number;
  totalGoods: number;
  totalToPay: number;
}

export interface StatusOption {
  label: string;
  value: PurchaseStatus | null;
}

export interface CategoryOption {
  label: string;
  value: InvoiceCategory;
}

export interface UsePurchasesListPageReturn {
  data: PurchaseListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  search: string;
  setSearch: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  statusFilter: PurchaseStatus | null;
  setStatusFilter: (value: PurchaseStatus | null) => void;
  categoryFilter: InvoiceCategory | null;
  setCategoryFilter: (value: InvoiceCategory | null) => void;
  statusOptions: StatusOption[];
  categoryOptions: CategoryOption[];
  activeFilters: boolean;
  locale: Locale;
  filteredPurchases: PurchaseListItem[];
  sortConfig: SortConfig<SortKey> | null;
  handleSort: (key: SortKey) => void;
  pagination: ReturnType<typeof usePagination>;
  pagePurchases: PurchaseListItem[];
  handleResetFilters: () => void;
  stats: PurchaseStats;
  t: ReturnType<typeof useTranslation>["t"];
}

export function usePurchasesListPage(): UsePurchasesListPageReturn {
  const { i18n, t } = useTranslation("PurchasesPage");
  const locale = i18n.language as Locale;

  const { data: invoicesData, isLoading, isError } = useGetInvoicesQuery();
  const data = invoicesData?.results;

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [statusFilter, setStatusFilter] = useState<PurchaseStatus | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<InvoiceCategory | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig<SortKey> | null>(null);

  const statusOptions = useMemo<StatusOption[]>(() => {
    return [
      { label: t("filters.allStatuses"), value: null },
      { label: t("status.draft"), value: "draft" },
      { label: t("status.approved"), value: "approved" },
      { label: t("status.paid"), value: "paid" },
    ];
  }, [t]);

  const categoryOptions = useMemo<CategoryOption[]>(() => {
    return [
      { label: t("category.goods"), value: "goods" },
      { label: t("category.fixed_assets"), value: "fixed_assets" },
      { label: t("category.intangible"), value: "intangible" },
      { label: t("category.services"), value: "services" },
      { label: t("category.purchase"), value: "purchase" },
    ];
  }, [t]);

  const stats = useMemo<PurchaseStats>(() => {
    if (!data) return { totalServices: 0, totalGoods: 0, totalToPay: 0 };

    let totalServices = 0;
    let totalGoods = 0;
    let totalToPay = 0;

    for (const item of data) {
      const status = transactionStatusToPurchaseStatus(item.transaction_status);
      if (status === "paid") {
        if (item.category === "7210") {
          totalServices++;
        } else {
          totalGoods++;
        }
      }
      if (status === "approved") {
        totalToPay += toNumber(item.total_amount);
      }
    }

    return { totalServices, totalGoods, totalToPay };
  }, [data]);

  const activeFilters = useMemo(() => {
    return !!search.trim() || !!dateRange.start || !!dateRange.end || !!statusFilter || !!categoryFilter;
  }, [search, dateRange, statusFilter, categoryFilter]);

  function handleSort(key: SortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  const filteredPurchases = useMemo(() => {
    if (!data) return [];
    return data.filter((invoice: PurchaseListItem) => {
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          invoice.vendor_name.toLowerCase().includes(searchLower) ||
          invoice.invoice_number.toLowerCase().includes(searchLower) ||
          invoice.vendor_bin.includes(search) ||
          invoice.internal_id.toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;
      }

      const invoiceStatus = transactionStatusToPurchaseStatus(invoice.transaction_status);
      if (statusFilter && invoiceStatus !== statusFilter) {
        return false;
      }

      if (categoryFilter && invoice.invoice_category !== categoryFilter) {
        return false;
      }

      if (dateRange.start || dateRange.end) {
        const purchaseDate = invoice.invoice_date ? new Date(invoice.invoice_date) : null;
        if (!purchaseDate) return false;

        const normalizedDate = normalizeDateToStartOfDay(purchaseDate);
        const matches = matchesDateRange(normalizedDate, dateRange, normalizeDateToStartOfDay, normalizeDateToEndOfDay);
        if (!matches) return false;
      }

      return true;
    });
  }, [data, search, statusFilter, categoryFilter, dateRange]);

  const sortedPurchases = usePurchasesSort(filteredPurchases, sortConfig);
  const pagination = usePagination(sortedPurchases.length, 10, [
    search,
    statusFilter,
    categoryFilter,
    dateRange,
    sortConfig,
  ]);
  const pagePurchases = sortedPurchases.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setStatusFilter(null);
    setCategoryFilter(null);
    setSortConfig(null);
  }

  return {
    data,
    isLoading,
    isError,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    statusOptions,
    categoryOptions,
    activeFilters,
    locale,
    filteredPurchases,
    sortConfig,
    handleSort,
    pagination,
    pagePurchases,
    handleResetFilters,
    stats,
    t,
  };
}

